import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, CalendarClock, Loader2, XCircle } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { PortalShell } from "@/pages/AdminLogin";
import { Field, inputProps } from "@/components/Field";

const InterviewConfirm = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);
  const [showReschedule, setShowReschedule] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");

  useEffect(() => {
    if (!token) return setState("invalid");
    axios.get(`${API}/interview/lookup`, { params: { token } })
      .then(({ data }) => {
        setInfo(data);
        setState(data.status === "confirmed" ? "already" : "ready");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const respond = async (action) => {
    setBusy(true);
    try {
      await axios.post(`${API}/interview/respond`, { action, note }, { params: { token } });
      setResult(action);
      setState("done");
    } catch (e) {
      if (e?.response?.status === 400) {
        setState("already");
      } else {
        setState("error");
      }
    } finally {
      setBusy(false);
    }
  };

  const when = info?.proposed_at
    ? new Date(info.proposed_at).toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
    : "";

  return (
    <PortalShell title="Interview Invitation" subtitle="New Beginnings Healthcare Services">
      <div className="mt-8">
        {state === "loading" && (
          <div className="flex justify-center py-8"><div className="h-8 w-8 animate-spin rounded-full border-2 border-blush-300 border-t-gold" /></div>
        )}

        {state === "invalid" && (
          <div className="text-center">
            <XCircle className="mx-auto h-12 w-12 text-blush-400" strokeWidth={1.5} />
            <p data-testid="interview-invalid" className="mt-4 text-sm text-charcoal-muted">
              This interview link is invalid or has expired. Please contact us at (856) 545-7393.
            </p>
          </div>
        )}

        {state === "already" && (
          <div className="text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-gold" strokeWidth={1.5} />
            <p className="mt-4 text-sm text-charcoal-muted">
              This interview has already been confirmed. We look forward to meeting you!
            </p>
          </div>
        )}

        {state === "ready" && info && (
          <div data-testid="interview-details">
            <p className="text-center text-sm text-charcoal-muted">Hi {info.first_name}, we'd love to meet you.</p>
            <div className="mt-5 rounded-2xl border border-gold/30 bg-gold-soft/50 p-6 text-center">
              <CalendarClock className="mx-auto h-8 w-8 text-gold" strokeWidth={1.4} />
              <p className="mt-3 font-serif text-xl text-charcoal">{when}</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-gold-deep">{info.mode} interview</p>
            </div>
            {!showReschedule ? (
              <div className="mt-6 grid gap-3">
                <button onClick={() => respond("confirm")} disabled={busy} data-testid="interview-confirm-button" className="btn-pink w-full disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Confirm This Time
                </button>
                <button onClick={() => setShowReschedule(true)} data-testid="interview-reschedule-toggle" className="btn-gold-outline w-full">
                  Request a Different Time
                </button>
              </div>
            ) : (
              <div className="mt-6">
                <Field label="What times work better for you?" htmlFor="ic-note">
                  <textarea id="ic-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Afternoons next week work best..." {...inputProps("interview-reschedule-note")} />
                </Field>
                <button onClick={() => respond("request_change")} disabled={busy} data-testid="interview-reschedule-submit" className="btn-pink mt-4 w-full disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Reschedule Request"}
                </button>
              </div>
            )}
          </div>
        )}

        {state === "done" && (
          <div className="text-center" data-testid="interview-done">
            <CheckCircle2 className="mx-auto h-12 w-12 text-gold" strokeWidth={1.5} />
            {result === "confirm" ? (
              <>
                <p className="mt-4 font-serif text-xl text-charcoal">Interview confirmed!</p>
                <p className="mt-2 text-sm text-charcoal-muted">
                  {info?.mode} interview on {when}. A confirmation email is on its way to you.
                </p>
              </>
            ) : (
              <>
                <p className="mt-4 font-serif text-xl text-charcoal">Request sent</p>
                <p className="mt-2 text-sm text-charcoal-muted">
                  Thank you — our team will reach out shortly with a new proposed time.
                </p>
              </>
            )}
          </div>
        )}

        {state === "error" && (
          <p className="text-center text-sm text-red-600">Something went wrong. Please call us at (856) 545-7393.</p>
        )}
      </div>
      <p className="mt-8 text-center text-xs text-charcoal-faint">
        <Link to="/" className="text-gold-deep hover:underline">newbeginningshealthcare.net</Link>
      </p>
    </PortalShell>
  );
};

export default InterviewConfirm;
